import {compactEth, formatDuration, formatEth, fetchBeacon, THRESHOLD_VOLUME} from '@colosseum/shared';
import {html, raw, setHtml, setText, $} from '../ui/dom.js';
import {drawRow, oddsBoard, tokenCard} from '../ui/components.js';
import {closeModal, notify, txProgress} from '../ui/feedback.js';
import {drawIsCallable, refresh, secondsToClose, state} from '../store.js';
import {api} from '../api.js';
import {submitDraw, submitExecute, submitSettle, describeError, revertName} from '../tx.js';
import {mountCombat} from '../combat.js';

/**
 * The arena page. What the old page called a fight between two coins is a
 * weighted lottery over every token that paid the threshold, so the two-fighter
 * bar is gone and the odds board is the real thing in its place. The fighters
 * stay as scenery, and say so.
 */

let stopCombat: () => void = () => {};
let paused = matchMedia('(prefers-reduced-motion: reduce)').matches;
let sort = 'fees';
let query = '';

export function arenaView(): string {
  return html`<div class="arena-layout">
    <aside class="arena-note">
      <p>EVERY TRADE PAYS 1.3%.<br>HALF A PERCENT POOLS.<br><strong>EVERY FOUR HOURS THE POOL BUYS ONE TOKEN AND BURNS IT.</strong></p>
      <p class="arena-note-sub">A token's chance is its share of the fees paid. Nobody chooses the winner, including us.</p>
    </aside>

    <section class="arena ${paused ? 'paused' : ''}" aria-label="The arena">
      <div class="arena-stats">
        <div class="pot">
          <div class="eyebrow">THE POOL</div>
          <div class="pot-amount" id="pot">—<span>ETH</span></div>
          <div class="pot-sub" id="pot-sub">spent in full at every draw</div>
        </div>
        <div>
          <div class="eyebrow">NEXT DRAW IN</div>
          <div class="countdown" id="countdown">—:—:—</div>
          <div class="pot-sub" id="round-note"></div>
        </div>
        <div class="community-count">
          <span id="qualified-count">—</span>
          <small>IN THE DRAW</small>
        </div>
      </div>

      <div class="arena-stage">
        <img class="arena-backdrop" src="/assets/arena.webp" alt="">
        <canvas id="combat" width="400" height="172" role="img"
          aria-label="Decorative pixel-art gladiators. The animation is scenery and has no bearing on the draw."></canvas>
        ${raw(
          Array.from({length: 15}, (_, i) =>
            `<i class="ember" style="left:${10 + i * 5.7}%;animation-delay:${i * 0.39}s" aria-hidden="true"></i>`,
          ).join(''),
        )}
        <span class="live"><i class="dot"></i>LIVE</span>
        <button class="motion-toggle" data-action="motion" aria-pressed="${paused}"
          aria-label="${paused ? 'Play' : 'Pause'} the animation">${paused ? '▶' : 'Ⅱ'}</button>
        <div class="scenery-note">scenery — the draw is decided by a randomness beacon</div>
      </div>

      <div class="odds-panel">
        <div class="odds-head">
          <b>THIS PERIOD'S DRAW</b>
          <span id="odds-weight"></span>
        </div>
        <div id="odds-board"><div class="skeleton-rows"></div></div>
        <div class="odds-foot" id="draw-cta"></div>
      </div>
    </section>
  </div>

  <section class="recent">
    <div class="section-head"><h2>Recent draws</h2><a href="#/docs">How the draw works →</a></div>
    <ol class="draw-list" id="draw-list"><li class="draw-row muted">Loading…</li></ol>
  </section>

  <div class="toolbar">
    <div class="filters" role="group" aria-label="Sort tokens">
      ${raw(
        [
          ['fees', 'Fees this period'],
          ['odds', 'Best odds'],
          ['volume', 'Volume 24h'],
          ['new', 'New'],
          ['graduating', 'Closest to graduating'],
          ['marketCap', 'Market value'],
        ]
          .map(
            ([k, label]) =>
              `<button class="filter ${sort === k ? 'active' : ''}" data-action="sort" data-sort="${k}"
                aria-pressed="${sort === k}">${label}</button>`,
          )
          .join(''),
      )}
    </div>
  </div>
  <section id="token-grid" class="token-grid" aria-label="Tokens"></section>`;
}

export function mountArena(): () => void {
  stopCombat = mountCombat($<HTMLCanvasElement>('#combat'), () => paused);
  const tick = setInterval(paintCountdown, 1000);
  paint();
  paintCountdown();
  return () => {
    stopCombat();
    clearInterval(tick);
  };
}

function paintCountdown(): void {
  const a = state.arena;
  const el = $('#countdown');
  if (!el) return;
  if (!a?.started) {
    setText(el, '—:—:—');
    setText($('#round-note'), 'the clock starts with the first launch');
    return;
  }
  const left = secondsToClose();
  setText(el, left > 0 ? formatDuration(left) : 'CLOSED');
  el.classList.toggle('overdue', left === 0);
  if (a.pending) {
    const p = a.pending;
    setText(
      $('#round-note'),
      `period ${p.period} drawn: buying ${p.symbol ?? 'the winner'}, ${formatEth(BigInt(p.spentWei), {places: 4})} ETH spent over ${p.slices} slice${p.slices === 1 ? '' : 's'}`,
    );
  } else if (left > 0) {
    setText($('#round-note'), `period ${a.nextPeriod} · settled by drand round ${a.round}`);
  } else if (drawIsCallable()) {
    setText($('#round-note'), `round ${a.round} is published — anyone can run the draw`);
  } else {
    const wait = Math.max(0, a.roundPublishTime - Math.floor(Date.now() / 1000));
    setText($('#round-note'), `waiting for drand round ${a.round}, about ${wait}s away`);
  }
  paintDrawCta();
}

function paintDrawCta(): void {
  const el = $('#draw-cta');
  if (!el) return;
  const pending = state.arena?.pending;
  if (pending) {
    setHtml(
      el,
      html`<div class="draw-ready">
        <span><b>Period ${pending.period} is settled: ${pending.symbol ?? 'the winner'} was drawn.</b> The pool is being
          spent on it in slices of at most a 10% price move each. Anyone may run the next slice; the next period
          cannot settle until this purchase is complete.</span>
        <button class="primary" data-action="run-execute">Run the next slice</button>
      </div>`,
    );
    return;
  }
  if (!drawIsCallable()) {
    setHtml(
      el,
      html`<span class="odds-hint">The pool is spent in full at every draw. Anyone may run it — no operator can
        delay a draw or withhold the pool.</span>`,
    );
    return;
  }
  setHtml(
    el,
    html`<div class="draw-ready">
      <span><b>Period ${state.arena?.nextPeriod} is ready to draw.</b> The beacon is published and the result is
        already determined; running it only puts it on chain and starts the purchase.</span>
      <button class="primary" data-action="run-draw">Run the draw</button>
    </div>`,
  );
}

export function paint(): void {
  const a = state.arena;
  if (a) {
    setHtml($('#pot'), html`${compactEth(BigInt(a.potWei))}<span>ETH</span>`);
    setText($('#qualified-count'), String(a.qualifiedCount));
  }
  const p = state.period;
  if (p) {
    setHtml(
      $('#odds-board'),
      oddsBoard(p.entries, p.belowThreshold, THRESHOLD_VOLUME ? (Number(THRESHOLD_VOLUME) / 1e18).toFixed(2) : '3.85'),
    );
    setText(
      $('#odds-weight'),
      p.entries.length ? `${formatEth(BigInt(p.weightWei), {places: 4})} ETH of fees in the draw` : '',
    );
  }
  setHtml(
    $('#draw-list'),
    state.draws.length
      ? state.draws.map((d) => drawRow(d)).join('')
      : html`<li class="draw-row muted">No draws yet. The first is four hours after the first launch.</li>`,
  );
  paintGrid();
}

function paintGrid(): void {
  const grid = $('#token-grid');
  if (!grid) return;
  const q = query.trim().toLowerCase();
  const source = q && searchResults ? searchResults : state.tokens;
  const rows = source
    .filter((t) => !q || `${t.name} ${t.symbol} ${t.address}`.toLowerCase().includes(q))
    .slice()
    .sort(sorter());
  setHtml(
    grid,
    rows.length
      ? rows.map(tokenCard).join('')
      : html`<div class="empty">${state.source === 'down'
          ? 'Cannot reach the indexer or the chain.'
          : q
            ? 'Nothing matches that.'
            : 'No tokens launched yet.'}</div>`,
  );
}

function sorter(): (a: (typeof state.tokens)[number], b: (typeof state.tokens)[number]) => number {
  const big = (x: string, y: string) => (BigInt(y) > BigInt(x) ? 1 : BigInt(y) < BigInt(x) ? -1 : 0);
  switch (sort) {
    case 'odds': return (a, b) => b.oddsPpm - a.oddsPpm;
    case 'volume': return (a, b) => big(a.volume24hWei, b.volume24hWei);
    case 'new': return (a, b) => b.createdAt - a.createdAt;
    case 'marketCap': return (a, b) => big(a.marketCapWei, b.marketCapWei);
    case 'graduating':
      return (a, b) => Number(a.graduated) - Number(b.graduated) || b.progressPpm - a.progressPpm;
    default: return (a, b) => big(a.feesPeriodWei, b.feesPeriodWei);
  }
}

export function setSort(next: string): void {
  sort = next;
  for (const b of document.querySelectorAll<HTMLElement>('[data-action="sort"]')) {
    const on = b.dataset.sort === next;
    b.classList.toggle('active', on);
    b.setAttribute('aria-pressed', String(on));
  }
  paintGrid();
}

let searchTimer: ReturnType<typeof setTimeout> | undefined;
let searchResults: typeof state.tokens | null = null;

/**
 * Filters what is already loaded straight away, then asks the indexer, which
 * searches every launch rather than the page the arena happens to be holding.
 */
export function setQuery(next: string): void {
  query = next;
  paintGrid();
  clearTimeout(searchTimer);
  if (!next.trim()) {
    searchResults = null;
    return;
  }
  searchTimer = setTimeout(async () => {
    const asked = next;
    try {
      const rows = await api.tokens({q: asked.trim(), sort, limit: 60});
      if (asked !== query) return; // the box moved on
      searchResults = rows;
      paintGrid();
    } catch {
      /* the local filter stands */
    }
  }, 250);
}

export function toggleMotion(): void {
  paused = !paused;
  $('.arena')?.classList.toggle('paused', paused);
  const b = $('[data-action="motion"]');
  if (b) {
    b.textContent = paused ? '▶' : 'Ⅱ';
    b.setAttribute('aria-pressed', String(paused));
  }
}

/**
 * Runs the draw from the browser. The beacon is fetched from drand's public
 * interface and verified on chain, so a bad response cannot do more than make
 * the transaction revert.
 *
 * `draw` settles and runs the first purchase slice together, and reverts as a
 * whole if the slice cannot run in this block. The winner is then recorded with
 * `settle` alone, and the purchase is run slice by slice afterwards.
 */
export async function runDraw(): Promise<void> {
  const a = state.arena;
  if (!a) return;
  const p = txProgress(`Draw for period ${a.nextPeriod}`);
  try {
    p.update('simulating', `Fetching drand round ${a.round}…`);
    const {signature} = await fetchBeacon(BigInt(a.round));
    try {
      await submitDraw(signature, (stage, detail, hash) => p.update(stage, detail, hash));
    } catch (e) {
      if (revertName(e) !== 'NoProgress') throw e;
      await submitSettle(signature, (stage, detail, hash) => p.update(stage, detail, hash));
    }
    await refresh();
    closeModal();
    const pending = state.arena?.pending;
    notify(
      pending
        ? `Period ${pending.period} is settled: ${pending.symbol ?? 'the winner'} was drawn. The purchase runs in slices; anyone can run the next one.`
        : 'The draw is on chain. The pool was spent and what it bought is burned.',
      'success',
    );
  } catch (e) {
    p.fail(describeError(e));
  }
}

/** Runs one purchase slice for the settled winner. */
export async function runExecute(): Promise<void> {
  const pending = state.arena?.pending;
  if (!pending) return;
  const p = txProgress(`Purchase slice for period ${pending.period}`);
  try {
    await submitExecute((stage, detail, hash) => p.update(stage, detail, hash));
    await refresh();
    closeModal();
    notify(
      state.arena?.pending
        ? 'Slice done. The pool is not yet spent; run another whenever you like.'
        : 'The purchase is complete. The pool was spent and what it bought is burned.',
      'success',
    );
  } catch (e) {
    p.fail(describeError(e));
  }
}
